import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  X, AlertTriangle, Printer, Download, Share2, Trash2, Package,
  ShoppingCart, Sliders, Building2, Loader2, Plus, History, Save,
  Clock, ChevronLeft,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (n) => Number(n || 0).toLocaleString("en-US", { maximumFractionDigits: 0 });
const fmtDT = (iso) => { try { return new Date(iso).toLocaleString("en-GB", { hour12: false }); } catch { return iso; } };

/**
 * Smart low-stock alert per company. Given a brand, fetches its products
 * and lists the ones whose stock is at or below the admin-configurable
 * threshold (default 3 cartons). Admin can:
 *  • adjust the threshold with a slider — the list re-filters live
 *  • edit desired-order quantity per row
 *  • trash-delete a row they don't want to reorder
 *  • ADD any manual item (even not in system) via the ➕ button
 *  • save the resulting list to history & recall past lists via 📜
 *  • export the resulting shopping list to Print / PDF / WhatsApp
 *
 * The saved-history modal lets the admin recall a snapshot they built
 * days ago — useful to compare "what was ordered" vs "what arrived".
 */
const LowStockAlertModal = ({ brand, onClose }) => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [threshold, setThreshold] = useState(3);
  // rows the admin is actively editing: id → { qty, deleted }
  const [drafts, setDrafts] = useState({});
  // manual rows added by the admin (not sourced from products collection)
  const [manuals, setManuals] = useState([]); // [{id, name, order_qty}]
  const [addingManual, setAddingManual] = useState(false);
  const [manualName, setManualName] = useState("");
  const [manualQty, setManualQty] = useState(1);

  // history side-panel
  const [historyOpen, setHistoryOpen] = useState(false);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [viewSnapshot, setViewSnapshot] = useState(null); // one saved list to display

  // Image resolver — supports either external URL or object-storage path.
  const imgSrc = (p) => {
    if (!p) return "";
    if (p.image_url) return p.image_url;
    if (p.image_path) return `${API}/files/${p.image_path}`;
    return "";
  };

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/admin/products`);
        const all = Array.isArray(data) ? data : (data.products || []);
        // Filter to this brand (either by brand_id or brand name fallback)
        const forBrand = all.filter((p) => {
          if (brand.id === "__none__") return !p.brand_id && !p.brand;
          return p.brand_id === brand.id || p.brand === brand.name_ar;
        });
        setProducts(forBrand);
      } catch (e) {
        toast.error("فشل تحميل المنتجات");
      } finally { setLoading(false); }
    })();
  }, [brand.id, brand.name_ar]);

  const loadHistory = async () => {
    setHistoryLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/shortage-lists`, {
        params: { brand_id: brand.id },
      });
      setHistory(Array.isArray(data) ? data : []);
    } catch { toast.error("فشل تحميل السجل"); }
    finally { setHistoryLoading(false); }
  };
  useEffect(() => { if (historyOpen) loadHistory(); }, [historyOpen]); // eslint-disable-line

  // Low-stock items = products ≤ threshold AND not manually deleted from list
  const items = useMemo(() => {
    const auto = products
      .filter((p) => Number(p.stock || 0) <= threshold && !drafts[p.id]?.deleted)
      .map((p) => {
        const draft = drafts[p.id] || {};
        const suggested = Math.max(1, threshold * 2 - Number(p.stock || 0));
        const orderQty = draft.qty ?? suggested;
        return {
          _key: p.id,
          id: p.id,
          product_id: p.id,
          name: p.name_ar,
          brand: p.brand,
          image_url: p.image_url,
          image_path: p.image_path,
          currentStock: Number(p.stock || 0),
          orderQty: Number(orderQty),
          manual: false,
        };
      });
    const mans = manuals.map((m) => ({
      _key: m.id,
      id: m.id,
      product_id: "",
      name: m.name,
      currentStock: 0,
      orderQty: Number(m.order_qty || 0),
      manual: true,
    }));
    return [...auto, ...mans].sort((a, b) => a.currentStock - b.currentStock);
  }, [products, threshold, drafts, manuals]);

  const setQty = (id, qty) => setDrafts((d) => ({ ...d, [id]: { ...d[id], qty } }));
  const removeRow = (id, isManual) => {
    if (isManual) setManuals((m) => m.filter((x) => x.id !== id));
    else setDrafts((d) => ({ ...d, [id]: { ...d[id], deleted: true } }));
  };
  const addManual = () => {
    const nm = manualName.trim();
    const qty = Number(manualQty);
    if (!nm) { toast.error("اكتب اسم المادة"); return; }
    if (!qty || qty <= 0) { toast.error("العدد المطلوب لازم يكون أكبر من صفر"); return; }
    setManuals((m) => [...m, { id: `m-${Date.now()}`, name: nm, order_qty: qty }]);
    setManualName(""); setManualQty(1); setAddingManual(false);
  };

  const saveSnapshot = async (silent = false) => {
    if (items.length === 0) return null;
    try {
      const payload = {
        brand_id: brand.id,
        brand_name: brand.name_ar,
        threshold,
        items: items.map((it) => ({
          product_id: it.product_id,
          name: it.name,
          current_stock: it.currentStock,
          order_qty: it.orderQty,
          manual: it.manual,
        })),
      };
      const { data } = await axios.post(`${API}/admin/shortage-lists`, payload);
      if (!silent) toast.success("تم حفظ القائمة بالسجل");
      return data.list;
    } catch (e) {
      if (!silent) toast.error(e?.response?.data?.detail || "فشل الحفظ");
      return null;
    }
  };

  // Brand-driven accent (fall back to orange if no color).
  const accent = brand.color || "#F5A623";

  const buildText = (rows = items) => {
    const header = `📋 قائمة النواقص — ${brand.name_ar}\n📅 ${new Date().toLocaleString("en-GB", { hour12: false })}\n─────────────`;
    const body = rows.map((it, i) => {
      return `${i + 1}. ${it.name}\n   • المطلوب: ${fmt(it.orderQty)} كرتون`;
    }).join("\n\n");
    const footer = `─────────────\n📦 عدد الأصناف: ${rows.length}\n🛒 إجمالي الكراتين المطلوبة: ${fmt(rows.reduce((s, it) => s + Number(it.orderQty), 0))}`;
    return `${header}\n${body}\n${footer}`;
  };

  const openWhatsapp = async () => {
    if (items.length === 0) { toast.error("لا توجد أصناف ناقصة"); return; }
    await saveSnapshot(true);
    window.open(`https://wa.me/?text=${encodeURIComponent(buildText())}`, "_blank");
  };

  const doPrint = async (rows = null, title = null) => {
    const list = rows || items;
    if (list.length === 0) { toast.error("لا توجد أصناف ناقصة"); return; }
    // Save snapshot only when printing the live list (not when re-printing history)
    if (!rows) await saveSnapshot(true);
    const w = window.open("", "_blank");
    if (!w) return;
    const esc = (s) => String(s ?? "").replace(/</g, "&lt;").replace(/>/g, "&gt;");
    const tRows = list.map((it, i) => `<tr>
      <td class=num>${i + 1}</td>
      <td class=name>
        <div class=nm>${esc(it.name)}</div>
        ${it.brand ? `<div class=br>${esc(it.brand)}</div>` : ""}
      </td>
      <td class=ord>${fmt(it.orderQty)}</td>
    </tr>`).join("");
    const totalCartons = list.reduce((s, it) => s + Number(it.orderQty), 0);
    const heading = title || `قائمة النواقص — ${brand.name_ar}`;
    w.document.write(`<!doctype html><html dir=rtl><head><meta charset=utf-8>
      <title>${esc(heading)}</title>
      <style>
        *{box-sizing:border-box}
        body{font-family:'Segoe UI',Tahoma,Arial;padding:20px;color:#1e293b;background:white}
        h1{background:linear-gradient(135deg,${accent},#e85d45);color:white;text-align:center;font-size:22px;margin:0;padding:14px;border-radius:12px}
        .sub{text-align:center;color:#64748b;font-size:12px;margin:10px 0 16px}
        table{width:100%;border-collapse:collapse;font-size:14px;box-shadow:0 2px 8px rgba(0,0,0,.06);border-radius:10px;overflow:hidden}
        th{background:linear-gradient(135deg,${accent},#e85d45);color:white;padding:12px 8px;text-align:right;font-size:13px}
        td{padding:12px 8px;border-bottom:1px solid #e2e8f0;vertical-align:middle}
        tr:nth-child(even) td{background:#fafbfc}
        .num{text-align:center;background:${accent}15;color:${accent};font-weight:900;width:44px;font-size:15px}
        .name{min-width:220px}.nm{font-weight:900;font-size:15px}.br{color:#94a3b8;font-size:11px;margin-top:2px}
        .ord{text-align:center;color:#059669;font-weight:900;font-size:18px;width:120px}
        tfoot td{background:linear-gradient(135deg,${accent}25,#e85d4525);color:${accent};font-weight:900;padding:14px 8px;font-size:16px}
        @media print{body{padding:8px}}
      </style></head><body>
      <h1>📋 ${esc(heading)}</h1>
      <p class=sub>${new Date().toLocaleString("en-GB", { hour12: false })}</p>
      <table>
        <thead><tr><th>#</th><th>اسم المنتج</th><th>المطلوب (كرتون)</th></tr></thead>
        <tbody>${tRows}</tbody>
        <tfoot>
          <tr><td colspan=2>الإجمالي — ${list.length} صنف</td><td>${fmt(totalCartons)}</td></tr>
        </tfoot>
      </table>
      <script>window.print();</script></body></html>`);
    w.document.close();
  };

  // ── Render helpers ───────────────────────────────────────────────────
  const renderSnapshotView = () => {
    const snapItems = (viewSnapshot.items || []).map((it, i) => ({
      _key: `${viewSnapshot.id}-${i}`,
      name: it.name,
      orderQty: Number(it.order_qty || 0),
      currentStock: Number(it.current_stock || 0),
      manual: !!it.manual,
    }));
    const total = snapItems.reduce((s, it) => s + Number(it.orderQty), 0);
    return (
      <div className="flex-1 overflow-y-auto p-3 bg-slate-50 space-y-2">
        <div className="rounded-2xl bg-white p-3 border-2 shadow-sm" style={{ borderColor: `${accent}30` }}>
          <div className="flex items-center justify-between mb-2">
            <div>
              <p className="font-black text-slate-800 text-sm">{viewSnapshot.brand_name}</p>
              <p className="text-[11px] text-slate-500 flex items-center gap-1 mt-0.5">
                <Clock className="w-3 h-3" /> {fmtDT(viewSnapshot.created_at)}
              </p>
              {viewSnapshot.created_by_name && (
                <p className="text-[11px] text-slate-500 mt-0.5">✍️ {viewSnapshot.created_by_name}</p>
              )}
            </div>
            <div className="text-center">
              <p className="text-[10px] text-slate-500 font-bold">إجمالي</p>
              <p className="text-lg font-black text-emerald-700">{fmt(total)}</p>
            </div>
          </div>
        </div>
        {snapItems.map((it, i) => (
          <div key={it._key} className="bg-white rounded-xl p-3 border shadow-sm flex items-center gap-3">
            <div className="w-8 h-8 rounded-full flex items-center justify-center font-black text-xs text-white shrink-0"
              style={{ background: accent }}>{i + 1}</div>
            <p className="flex-1 font-bold text-sm text-slate-800 truncate">{it.name}</p>
            <span className="text-sm font-black text-emerald-700" dir="ltr">{fmt(it.orderQty)} كرتون</span>
          </div>
        ))}
      </div>
    );
  };

  const renderHistoryList = () => (
    <div className="flex-1 overflow-y-auto p-3 bg-slate-50 space-y-2">
      {historyLoading ? (
        <div className="text-center py-10"><Loader2 className="w-8 h-8 animate-spin inline text-slate-400" /></div>
      ) : history.length === 0 ? (
        <div className="text-center py-12 text-slate-400 text-sm">
          <History className="w-12 h-12 mx-auto mb-2 opacity-30" />
          <p className="font-bold text-slate-500">لا توجد قوائم محفوظة</p>
          <p className="text-xs">القوائم اللي تحفظها تظهر هنا</p>
        </div>
      ) : (
        history.map((h) => (
          <button key={h.id}
            type="button"
            onClick={() => setViewSnapshot(h)}
            data-testid={`shortage-history-${h.id}`}
            className="w-full text-right bg-white rounded-2xl p-3 border-2 shadow-sm hover:shadow-md transition-all flex items-center gap-3"
            style={{ borderColor: `${accent}30` }}>
            <div className="w-11 h-11 rounded-xl flex items-center justify-center shrink-0 text-white"
              style={{ background: accent }}>
              <ShoppingCart className="w-5 h-5" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-black text-sm text-slate-800 truncate">
                {(h.items || []).length} صنف • {fmt(h.total_qty)} كرتون
              </p>
              <p className="text-[11px] text-slate-500 flex items-center gap-1 mt-0.5">
                <Clock className="w-3 h-3" /> {fmtDT(h.created_at)}
              </p>
              {h.created_by_name && (
                <p className="text-[11px] text-slate-500 truncate mt-0.5">✍️ {h.created_by_name}</p>
              )}
            </div>
            <ChevronLeft className="w-5 h-5 text-slate-400 shrink-0" />
          </button>
        ))
      )}
    </div>
  );

  return (
    <div className="fixed inset-0 z-[130] bg-black/60 flex items-center justify-center p-3" dir="rtl" data-testid="low-stock-modal">
      <div className="bg-white rounded-3xl w-full max-w-2xl max-h-[92vh] overflow-hidden shadow-2xl flex flex-col">
        {/* Header */}
        <div className="p-4 text-white relative overflow-hidden"
          style={{ background: `linear-gradient(135deg, ${accent}, #e85d45)` }}>
          <div className="absolute -top-6 -left-6 w-28 h-28 rounded-full bg-white/10" />
          <div className="absolute -bottom-8 -right-4 w-24 h-24 rounded-full bg-white/5" />
          <div className="relative z-10 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-2xl bg-white/25 backdrop-blur flex items-center justify-center border-2 border-white/40">
                <AlertTriangle className="w-6 h-6" strokeWidth={2.5} />
              </div>
              <div>
                <p className="text-xs opacity-90">
                  {viewSnapshot ? "قائمة محفوظة" : historyOpen ? "السجل" : "قائمة النواقص"}
                </p>
                <h3 className="font-black text-xl flex items-center gap-1.5">
                  <Building2 className="w-5 h-5" />
                  {brand.name_ar}
                </h3>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {(viewSnapshot || historyOpen) && (
                <button
                  onClick={() => { if (viewSnapshot) setViewSnapshot(null); else setHistoryOpen(false); }}
                  data-testid="shortage-back-btn"
                  className="w-10 h-10 rounded-full bg-white/25 hover:bg-white/40 backdrop-blur flex items-center justify-center"
                  title="رجوع"
                >
                  <ChevronLeft className="w-5 h-5 rotate-180" />
                </button>
              )}
              {!historyOpen && !viewSnapshot && (
                <button
                  onClick={() => setHistoryOpen(true)}
                  data-testid="shortage-history-btn"
                  className="w-10 h-10 rounded-full bg-white/25 hover:bg-white/40 backdrop-blur flex items-center justify-center"
                  title="القوائم السابقة"
                >
                  <History className="w-5 h-5" />
                </button>
              )}
              <button onClick={onClose} data-testid="low-stock-close"
                className="w-10 h-10 rounded-full bg-white/25 hover:bg-white/40 backdrop-blur flex items-center justify-center">
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>

        {/* Snapshot view — read-only saved list */}
        {viewSnapshot ? (
          <>
            {renderSnapshotView()}
            <div className="p-3 bg-white border-t border-slate-100 grid grid-cols-3 gap-2">
              <button
                onClick={() => {
                  const list = (viewSnapshot.items || []).map((it, i) => ({
                    _key: `${viewSnapshot.id}-${i}`, name: it.name, orderQty: Number(it.order_qty || 0),
                    currentStock: 0, manual: !!it.manual,
                  }));
                  window.open(`https://wa.me/?text=${encodeURIComponent(buildText(list))}`, "_blank");
                }}
                className="py-2.5 rounded-xl bg-green-100 hover:bg-green-200 text-green-800 text-sm font-bold flex items-center justify-center gap-1.5">
                <Share2 className="w-4 h-4" /> واتساب
              </button>
              <button
                onClick={() => {
                  const list = (viewSnapshot.items || []).map((it, i) => ({
                    _key: `${viewSnapshot.id}-${i}`, name: it.name, orderQty: Number(it.order_qty || 0),
                    currentStock: 0, manual: !!it.manual,
                  }));
                  doPrint(list, `قائمة محفوظة — ${viewSnapshot.brand_name}`);
                }}
                className="py-2.5 rounded-xl bg-red-100 hover:bg-red-200 text-red-800 text-sm font-bold flex items-center justify-center gap-1.5">
                <Download className="w-4 h-4" /> PDF
              </button>
              <button
                onClick={async () => {
                  if (!window.confirm("حذف هذه القائمة من السجل؟")) return;
                  try {
                    await axios.delete(`${API}/admin/shortage-lists/${viewSnapshot.id}`);
                    toast.success("تم الحذف");
                    setViewSnapshot(null);
                    loadHistory();
                  } catch { toast.error("فشل الحذف"); }
                }}
                className="py-2.5 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700 text-sm font-bold flex items-center justify-center gap-1.5">
                <Trash2 className="w-4 h-4" /> حذف
              </button>
            </div>
          </>
        ) : historyOpen ? (
          renderHistoryList()
        ) : (
          <>
            {/* Threshold slider */}
            <div className="px-4 py-3 bg-gradient-to-l from-amber-50 to-orange-50 border-b border-amber-100">
              <div className="flex items-center justify-between mb-1.5">
                <label className="text-xs font-black text-slate-700 flex items-center gap-1.5">
                  <Sliders className="w-4 h-4" style={{ color: accent }} />
                  الحد الأدنى للنقص (كرتون)
                </label>
                <span className="text-2xl font-black" style={{ color: accent }}>{threshold}</span>
              </div>
              <input
                type="range" min="1" max="20" value={threshold}
                onChange={(e) => setThreshold(Number(e.target.value))}
                data-testid="low-stock-threshold"
                className="w-full h-2 rounded-full appearance-none cursor-pointer"
                style={{ background: `linear-gradient(to left, ${accent} ${(threshold / 20) * 100}%, #e2e8f0 ${(threshold / 20) * 100}%)` }}
              />
              <div className="flex items-center justify-between text-[10px] text-slate-500 mt-1">
                <span>1</span><span>5</span><span>10</span><span>15</span><span>20</span>
              </div>
            </div>

            {/* Summary strip */}
            <div className="px-4 py-2 bg-white border-b border-slate-100 grid grid-cols-2 gap-2">
              <div className="rounded-xl p-2 text-center border-2" style={{ background: `${accent}0d`, borderColor: `${accent}40` }}>
                <p className="text-[10px] font-bold" style={{ color: accent }}>عدد الأصناف الناقصة</p>
                <p className="text-lg font-black" style={{ color: accent }}>{items.length}</p>
              </div>
              <div className="rounded-xl p-2 text-center border-2 bg-emerald-50 border-emerald-200">
                <p className="text-[10px] font-bold text-emerald-700">إجمالي الكراتين المطلوبة</p>
                <p className="text-lg font-black text-emerald-700">
                  {fmt(items.reduce((s, it) => s + Number(it.orderQty), 0))}
                </p>
              </div>
            </div>

            {/* Manual add row */}
            <div className="px-3 py-2 bg-white border-b border-slate-100">
              {!addingManual ? (
                <button
                  type="button"
                  onClick={() => setAddingManual(true)}
                  data-testid="low-stock-add-manual"
                  className="w-full py-2 rounded-xl border-2 border-dashed text-sm font-black flex items-center justify-center gap-2 transition-all hover:bg-orange-50"
                  style={{ borderColor: `${accent}60`, color: accent }}
                >
                  <Plus className="w-4 h-4" /> إضافة مادة يدوياً
                </button>
              ) : (
                <div className="flex items-center gap-2">
                  <input
                    autoFocus
                    value={manualName}
                    onChange={(e) => setManualName(e.target.value)}
                    data-testid="low-stock-manual-name"
                    placeholder="اسم المادة"
                    className="flex-1 px-3 py-2 rounded-xl border-2 border-orange-200 focus:border-orange-500 bg-white text-sm font-bold focus:outline-none text-slate-900"
                  />
                  <input
                    type="number" min="1" value={manualQty}
                    onChange={(e) => setManualQty(e.target.value)}
                    data-testid="low-stock-manual-qty"
                    placeholder="كرتون"
                    className="w-24 px-3 py-2 rounded-xl border-2 border-emerald-200 focus:border-emerald-500 text-sm font-black text-emerald-700 text-center focus:outline-none"
                    dir="ltr"
                  />
                  <button
                    onClick={addManual}
                    data-testid="low-stock-manual-save"
                    className="px-4 py-2 rounded-xl text-white text-sm font-black flex items-center gap-1 shrink-0"
                    style={{ background: accent }}
                  >
                    <Save className="w-4 h-4" /> حفظ
                  </button>
                  <button
                    onClick={() => { setAddingManual(false); setManualName(""); setManualQty(1); }}
                    className="w-9 h-9 rounded-xl bg-slate-100 hover:bg-slate-200 flex items-center justify-center shrink-0"
                  >
                    <X className="w-4 h-4 text-slate-500" />
                  </button>
                </div>
              )}
            </div>

            {/* Items list */}
            <div className="flex-1 overflow-y-auto p-3 space-y-2 bg-slate-50">
              {loading ? (
                <div className="text-center py-10">
                  <Loader2 className="w-8 h-8 animate-spin inline text-slate-400" />
                </div>
              ) : items.length === 0 ? (
                <div className="text-center py-12 text-slate-400 text-sm">
                  <Package className="w-12 h-12 mx-auto mb-2 opacity-30" />
                  <p className="font-bold text-slate-500">لا توجد نواقص ✅</p>
                  <p className="text-xs">كل منتجات هذه الشركة رصيدها فوق {threshold} كرتون</p>
                </div>
              ) : (
                items.map((it, i) => (
                  <div key={it._key}
                    data-testid={`low-stock-row-${it.id}`}
                    className="bg-white rounded-2xl p-3 border-2 shadow-sm flex items-center gap-3"
                    style={{ borderColor: it.manual ? "#3b82f680" : `${accent}30` }}>
                    <div className="w-9 h-9 rounded-full flex items-center justify-center font-black text-sm shrink-0 text-white"
                      style={{ background: it.manual ? "#3b82f6" : accent }}>
                      {i + 1}
                    </div>
                    {!it.manual && imgSrc(it) ? (
                      <img src={imgSrc(it)} alt={it.name}
                        loading="lazy" decoding="async"
                        className="w-14 h-14 object-cover rounded-xl border-2 border-slate-100 shrink-0" />
                    ) : (
                      <div className="w-14 h-14 rounded-xl bg-slate-100 flex items-center justify-center shrink-0">
                        <Package className="w-6 h-6 text-slate-400" />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="font-black text-sm text-slate-800 truncate">
                        {it.name}
                        {it.manual && <span className="mr-1 text-[10px] font-black text-blue-700 bg-blue-100 px-1.5 py-0.5 rounded"> يدوي</span>}
                      </p>
                      <div className="flex items-center gap-2 mt-1">
                        {!it.manual && (
                          <span className="text-[10px] px-2 py-0.5 rounded-full bg-red-50 text-red-700 font-bold" dir="ltr">
                            باقي: {it.currentStock}
                          </span>
                        )}
                        <input
                          type="number" min="1" value={it.orderQty}
                          onChange={(e) => {
                            if (it.manual) {
                              setManuals((m) => m.map((x) => x.id === it.id ? { ...x, order_qty: e.target.value } : x));
                            } else setQty(it.id, e.target.value);
                          }}
                          data-testid={`low-stock-qty-${it.id}`}
                          className="w-20 px-2 py-1 rounded-lg border-2 border-emerald-200 focus:outline-none focus:border-emerald-500 text-sm font-black text-center text-emerald-700"
                          dir="ltr"
                        />
                        <span className="text-[10px] text-slate-500">كرتون</span>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => removeRow(it.id, it.manual)}
                      data-testid={`low-stock-remove-${it.id}`}
                      title="حذف من القائمة"
                      className="w-8 h-8 rounded-lg bg-red-50 hover:bg-red-100 flex items-center justify-center shrink-0 transition-colors"
                    >
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </button>
                  </div>
                ))
              )}
            </div>

            {/* Actions — Print icon moved next to WhatsApp & PDF */}
            <div className="p-3 bg-white border-t border-slate-100 grid grid-cols-3 gap-2">
              <button onClick={openWhatsapp} disabled={items.length === 0} data-testid="low-stock-wa"
                className="py-2.5 rounded-xl bg-green-100 hover:bg-green-200 text-green-800 text-sm font-bold flex items-center justify-center gap-1.5 disabled:opacity-40">
                <Share2 className="w-4 h-4" /> واتساب
              </button>
              <button onClick={() => doPrint()} disabled={items.length === 0} data-testid="low-stock-pdf"
                className="py-2.5 rounded-xl bg-red-100 hover:bg-red-200 text-red-800 text-sm font-bold flex items-center justify-center gap-1.5 disabled:opacity-40">
                <Download className="w-4 h-4" /> PDF
              </button>
              <button onClick={() => doPrint()} disabled={items.length === 0} data-testid="low-stock-print"
                className="py-2.5 rounded-xl bg-blue-100 hover:bg-blue-200 text-blue-800 text-sm font-bold flex items-center justify-center gap-1.5 disabled:opacity-40">
                <Printer className="w-4 h-4" /> طباعة
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default LowStockAlertModal;
